import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { settingsService, locationService } from '../../services/api';

const Settings = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState('general');

    // Settings state
    const [generalSettings, setGeneralSettings] = useState({
                                                               companyName: '',
                                                               website: '',
                                                               phone: '',
                                                               address: '',
                                                               logo: null
                                                           });

    const [inventorySettings, setInventorySettings] = useState({
                                                                   lowStockThreshold: 10,
                                                                   enableAutoReorder: false,
                                                                   defaultLocationId: '',
                                                                   showReorderWarnings: true
                                                               });

    const [notificationSettings, setNotificationSettings] = useState({
                                                                         emailNotifications: true,
                                                                         lowStockAlerts: true,
                                                                         orderStatusUpdates: true,
                                                                         productionAlerts: true,
                                                                         weeklyReports: true
                                                                     });

    const [integrationSettings, setIntegrationSettings] = useState({
                                                                       shopifyEnabled: false,
                                                                       shopifyApiKey: '',
                                                                       shopifyApiSecret: '',
                                                                       shopifyWebhookUrl: '',
                                                                       quickBooksEnabled: false,
                                                                       quickBooksClientId: '',
                                                                       quickBooksClientSecret: '',
                                                                       shipstation_inventory_tracking: false,
                                                                       shipstation_inventory_location_id: ''
                                                                   });

    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [error, setError] = useState('');

    // Available locations for inventory settings
    const [locations, setLocations] = useState([]);

    useEffect(() => {
        const loadSettings = async () => {
            setLoading(true);
            try {
                // Load settings from API using the settingsService
                const [generalData, inventoryData, notificationData, integrationData, locationsData] = await Promise.all([
                                                                                                                             settingsService.getSettings('general'),
                                                                                                                             settingsService.getSettings('inventory'),
                                                                                                                             settingsService.getSettings('notifications'),
                                                                                                                             settingsService.getSettings('integrations'),
                                                                                                                             locationService.getLocations()
                                                                                                                         ]);

                setGeneralSettings(generalData);
                setInventorySettings(inventoryData);
                setNotificationSettings(notificationData);
                setIntegrationSettings(integrationData);
                setLocations(locationsData);
            } catch (error) {
                console.error("Error loading settings:", error);
                setError("Failed to load settings. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, []);

    const handleGeneralSettingsChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            setGeneralSettings(prev => ({
                ...prev,
                [name]: files[0]
            }));
        } else {
            setGeneralSettings(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleInventorySettingsChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            setInventorySettings(prev => ({
                ...prev,
                [name]: checked
            }));
        } else if (type === 'number') {
            setInventorySettings(prev => ({
                ...prev,
                [name]: parseInt(value, 10)
            }));
        } else {
            setInventorySettings(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleNotificationSettingsChange = (e) => {
        const { name, checked } = e.target;

        setNotificationSettings(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleIntegrationSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            setIntegrationSettings(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setIntegrationSettings(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSaveSettings = async () => {
        setSaveLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            // Determine which settings to save based on active category
            let category;
            let data;

            switch (activeCategory) {
                case 'general':
                    category = 'general';
                    data = generalSettings;
                    break;
                case 'inventory':
                    category = 'inventory';
                    data = inventorySettings;
                    break;
                case 'notifications':
                    category = 'notifications';
                    data = notificationSettings;
                    break;
                case 'integrations':
                    category = 'integrations';
                    data = integrationSettings;
                    break;
                default:
                    throw new Error('Invalid settings category');
            }

            // If logo is a File object, use FormData
            if (category === 'general' && generalSettings.logo instanceof File) {
                const formData = new FormData();
                Object.entries(data).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        formData.append(key, value);
                    }
                });

                await settingsService.updateSettings(category, formData);
            } else {
                await settingsService.updateSettings(category, data);
            }

            setSuccessMessage('Settings saved successfully!');

            // Hide success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (error) {
            console.error("Error saving settings:", error);
            setError('Failed to save settings. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    const testIntegration = async (integration) => {
        try {
            await settingsService.testIntegration(integration);
            alert(`${integration.charAt(0).toUpperCase() + integration.slice(1)} connection successful!`);
        } catch (error) {
            console.error(`Error testing ${integration} integration:`, error);
            alert(`Failed to connect to ${integration}. Please check your credentials.`);
        }
    };

    // Render the settings based on the active category
    const renderSettingsContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            );
        }

        switch (activeCategory) {
            case 'general':
                return (
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                                Company Name
                            </label>
                            <input
                                type="text"
                                id="companyName"
                                name="companyName"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={generalSettings.companyName}
                                onChange={handleGeneralSettingsChange}
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                                Website
                            </label>
                            <input
                                type="url"
                                id="website"
                                name="website"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={generalSettings.website}
                                onChange={handleGeneralSettingsChange}
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={generalSettings.phone}
                                onChange={handleGeneralSettingsChange}
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                                Business Address
                            </label>
                            <textarea
                                id="address"
                                name="address"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={generalSettings.address}
                                onChange={handleGeneralSettingsChange}
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="logo" className="block text-sm font-medium text-gray-700">
                                Company Logo
                            </label>
                            <input
                                type="file"
                                id="logo"
                                name="logo"
                                accept="image/*"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                onChange={handleGeneralSettingsChange}
                            />
                            {typeof generalSettings.logo === 'string' && generalSettings.logo && (
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">Current logo:</p>
                                    <img
                                        src={generalSettings.logo}
                                        alt="Company logo"
                                        className="mt-1 h-16 object-contain"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'inventory':
                return (
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label htmlFor="lowStockThreshold" className="block text-sm font-medium text-gray-700">
                                Low Stock Threshold
                            </label>
                            <input
                                type="number"
                                id="lowStockThreshold"
                                name="lowStockThreshold"
                                min="1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={inventorySettings.lowStockThreshold}
                                onChange={handleInventorySettingsChange}
                            />
                            <p className="text-xs text-gray-500">
                                Items with stock levels below this threshold will be flagged as low stock.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="enableAutoReorder"
                                    name="enableAutoReorder"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={inventorySettings.enableAutoReorder}
                                    onChange={handleInventorySettingsChange}
                                />
                                <label htmlFor="enableAutoReorder" className="ml-2 block text-sm text-gray-700">
                                    Enable Auto-Reorder
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">
                                Automatically generate purchase orders for items that fall below the threshold.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="defaultLocationId" className="block text-sm font-medium text-gray-700">
                                Default Inventory Location
                            </label>
                            <select
                                id="defaultLocationId"
                                name="defaultLocationId"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={inventorySettings.defaultLocationId}
                                onChange={handleInventorySettingsChange}
                            >
                                <option value="">Select a location</option>
                                {locations.map(location => (
                                    <option key={location.id} value={location.id}>
                                        {location.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="showReorderWarnings"
                                    name="showReorderWarnings"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={inventorySettings.showReorderWarnings}
                                    onChange={handleInventorySettingsChange}
                                />
                                <label htmlFor="showReorderWarnings" className="ml-2 block text-sm text-gray-700">
                                    Show Reorder Warnings
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">
                                Display warnings when parts need to be reordered in the dashboard.
                            </p>
                        </div>
                    </div>
                );

            case 'notifications':
                return (
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="emailNotifications"
                                    name="emailNotifications"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={notificationSettings.emailNotifications}
                                    onChange={handleNotificationSettingsChange}
                                />
                                <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-700">
                                    Email Notifications
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">
                                Enable email notifications for the system.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="lowStockAlerts"
                                    name="lowStockAlerts"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={notificationSettings.lowStockAlerts}
                                    onChange={handleNotificationSettingsChange}
                                />
                                <label htmlFor="lowStockAlerts" className="ml-2 block text-sm text-gray-700">
                                    Low Stock Alerts
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">
                                Send notifications when items reach low stock levels.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="orderStatusUpdates"
                                    name="orderStatusUpdates"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={notificationSettings.orderStatusUpdates}
                                    onChange={handleNotificationSettingsChange}
                                />
                                <label htmlFor="orderStatusUpdates" className="ml-2 block text-sm text-gray-700">
                                    Order Status Updates
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">
                                Send notifications when order statuses change.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="productionAlerts"
                                    name="productionAlerts"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={notificationSettings.productionAlerts}
                                    onChange={handleNotificationSettingsChange}
                                />
                                <label htmlFor="productionAlerts" className="ml-2 block text-sm text-gray-700">
                                    Production Alerts
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">
                                Send notifications for production events (batch completion, issues, etc.).
                            </p>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="weeklyReports"
                                    name="weeklyReports"
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    checked={notificationSettings.weeklyReports}
                                    onChange={handleNotificationSettingsChange}
                                />
                                <label htmlFor="weeklyReports" className="ml-2 block text-sm text-gray-700">
                                    Weekly Reports
                                </label>
                            </div>
                            <p className="text-xs text-gray-500">
                                Send weekly summary reports via email.
                            </p>
                        </div>
                    </div>
                );

            case 'integrations':
                return (
                    <div className="space-y-8">
                        <div className="p-4 border border-gray-200 rounded-md">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Shopify Integration</h3>

                            <div className="space-y-4">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="shopifyEnabled"
                                        name="shopifyEnabled"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={integrationSettings.shopifyEnabled}
                                        onChange={handleIntegrationSettingsChange}
                                    />
                                    <label htmlFor="shopifyEnabled" className="ml-2 block text-sm text-gray-700">
                                        Enable Shopify Integration
                                    </label>
                                </div>

                                {integrationSettings.shopifyEnabled && (
                                    <>
                                        <div className="space-y-2">
                                            <label htmlFor="shopifyApiKey" className="block text-sm font-medium text-gray-700">
                                                API Key
                                            </label>
                                            <input
                                                type="text"
                                                id="shopifyApiKey"
                                                name="shopifyApiKey"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={integrationSettings.shopifyApiKey}
                                                onChange={handleIntegrationSettingsChange}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label htmlFor="shopifyApiSecret" className="block text-sm font-medium text-gray-700">
                                                API Secret
                                            </label>
                                            <input
                                                type="password"
                                                id="shopifyApiSecret"
                                                name="shopifyApiSecret"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={integrationSettings.shopifyApiSecret}
                                                onChange={handleIntegrationSettingsChange}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label htmlFor="shopifyWebhookUrl" className="block text-sm font-medium text-gray-700">
                                                Webhook URL
                                            </label>
                                            <input
                                                type="url"
                                                id="shopifyWebhookUrl"
                                                name="shopifyWebhookUrl"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={integrationSettings.shopifyWebhookUrl}
                                                onChange={handleIntegrationSettingsChange}
                                            />
                                        </div>

                                        <div>
                                            <button
                                                type="button"
                                                className="mt-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                onClick={() => testIntegration('shopify')}
                                            >
                                                Test Connection
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border border-gray-200 rounded-md">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">QuickBooks Integration</h3>

                            <div className="space-y-4">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="quickBooksEnabled"
                                        name="quickBooksEnabled"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={integrationSettings.quickBooksEnabled}
                                        onChange={handleIntegrationSettingsChange}
                                    />
                                    <label htmlFor="quickBooksEnabled" className="ml-2 block text-sm text-gray-700">
                                        Enable QuickBooks Integration
                                    </label>
                                </div>

                                {integrationSettings.quickBooksEnabled && (
                                    <>
                                        <div className="space-y-2">
                                            <label htmlFor="quickBooksClientId" className="block text-sm font-medium text-gray-700">
                                                Client ID
                                            </label>
                                            <input
                                                type="text"
                                                id="quickBooksClientId"
                                                name="quickBooksClientId"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={integrationSettings.quickBooksClientId}
                                                onChange={handleIntegrationSettingsChange}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label htmlFor="quickBooksClientSecret" className="block text-sm font-medium text-gray-700">
                                                Client Secret
                                            </label>
                                            <input
                                                type="password"
                                                id="quickBooksClientSecret"
                                                name="quickBooksClientSecret"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                value={integrationSettings.quickBooksClientSecret}
                                                onChange={handleIntegrationSettingsChange}
                                            />
                                        </div>

                                        <div>
                                            <button
                                                type="button"
                                                className="mt-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                onClick={() => testIntegration('quickbooks')}
                                            >
                                                Test Connection
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="p-4 border border-gray-200 rounded-md">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">ShipStation Inventory Management</h3>

                            <div className="space-y-4">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="shipstation_inventory_tracking"
                                        name="shipstation_inventory_tracking"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={integrationSettings.shipstation_inventory_tracking}
                                        onChange={handleIntegrationSettingsChange}
                                    />
                                    <label htmlFor="shipstation_inventory_tracking" className="ml-2 block text-sm text-gray-700">
                                        Use ShipStation for Inventory Management
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500">
                                    When enabled, finished goods inventory will be tracked in ShipStation instead of locally.
                                    This means when production batches are completed, inventory quantities will be updated directly in ShipStation.
                                </p>

                                {integrationSettings.shipstation_inventory_tracking && (
                                    <div className="space-y-2 mt-4">
                                        <label htmlFor="shipstation_inventory_location_id" className="block text-sm font-medium text-gray-700">
                                            ShipStation Inventory Location ID
                                        </label>
                                        <input
                                            type="text"
                                            id="shipstation_inventory_location_id"
                                            name="shipstation_inventory_location_id"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            value={integrationSettings.shipstation_inventory_location_id}
                                            onChange={handleIntegrationSettingsChange}
                                            placeholder="Enter your ShipStation inventory location ID"
                                        />
                                        <p className="text-xs text-gray-500">
                                            The ShipStation location ID where production inventory will be updated. You can find this in your ShipStation inventory locations.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Settings</h1>

            <div className="md:grid md:grid-cols-4 md:gap-6">
                {/* Sidebar */}
                <div className="md:col-span-1">
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-4 py-5 sm:px-6">
                            <h2 className="text-lg font-medium text-gray-900">Settings Categories</h2>
                        </div>
                        <div className="border-t border-gray-200">
                            <nav className="flex flex-col">
                                <button
                                    className={`px-4 py-3 text-left text-sm ${
                                        activeCategory === 'general'
                                        ? 'bg-blue-50 text-blue-700 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                    onClick={() => setActiveCategory('general')}
                                >
                                    General Settings
                                </button>
                                <button
                                    className={`px-4 py-3 text-left text-sm ${
                                        activeCategory === 'inventory'
                                        ? 'bg-blue-50 text-blue-700 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                    onClick={() => setActiveCategory('inventory')}
                                >
                                    Inventory Settings
                                </button>
                                <button
                                    className={`px-4 py-3 text-left text-sm ${
                                        activeCategory === 'notifications'
                                        ? 'bg-blue-50 text-blue-700 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                    onClick={() => setActiveCategory('notifications')}
                                >
                                    Notification Settings
                                </button>
                                <button
                                    className={`px-4 py-3 text-left text-sm ${
                                        activeCategory === 'integrations'
                                        ? 'bg-blue-50 text-blue-700 font-medium'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    }`}
                                    onClick={() => setActiveCategory('integrations')}
                                >
                                    Integration Settings
                                </button>
                            </nav>
                        </div>
                        <div className="px-4 py-3 border-t border-gray-200">
                            <Link
                                to="/profile"
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                                My Profile Settings
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="mt-6 md:mt-0 md:col-span-3">
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-xl font-semibold text-gray-800">
                                {activeCategory === 'general' && 'General Settings'}
                                {activeCategory === 'inventory' && 'Inventory Settings'}
                                {activeCategory === 'notifications' && 'Notification Settings'}
                                {activeCategory === 'integrations' && 'Integration Settings'}
                            </h2>
                        </div>
                        <div className="p-6">
                            {/* Error and Success Messages */}
                            {error && (
                                <div className="mb-4 p-4 border border-red-400 bg-red-50 rounded-md text-red-700">
                                    {error}
                                </div>
                            )}

                            {successMessage && (
                                <div className="mb-4 p-4 border border-green-400 bg-green-50 rounded-md text-green-700">
                                    {successMessage}
                                </div>
                            )}

                            {/* Settings Content */}
                            {renderSettingsContent()}
                        </div>
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
                            <button
                                type="button"
                                className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                    saveLoading ? 'opacity-70 cursor-not-allowed' : ''
                                }`}
                                onClick={handleSaveSettings}
                                disabled={saveLoading || loading}
                            >
                                {saveLoading ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;
